import React, { useState } from "react";
import {
  Card,
  CardContent,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Typography,
  Box,
  Container,
  alpha,
  useTheme,
  Chip,
  Link,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  InsertDriveFile as FileIcon,
  GppGood as DnacprIcon,
  Gavel as LpaIcon,
  Security as DolsIcon,
} from "@mui/icons-material";
import DocumentsModal from "./DocumentsModal";
import { useMutation, useQuery } from "react-query";
import {
  createDnacpr,
  createLPA,
  deleteDnacpr,
  deleteDols,
  deleteLpa,
  getDnacpr,
  getDols,
  getLpa,
} from "../../../api/services/document";
import { enqueueSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import { QK } from "../../../base/qk";
import queryClient from "../../../base/QueryClient";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import DnacprEditModal from "./EditDnacprModal";
import EditLpaModal from "./EditLpaModal.js";
import EditDolsModal from "./EditDolsModal.js";
import DataTable from "../../Table/DataTable.js";

const Documents = () => {
  const theme = useTheme();
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const { id: clientId } = useParams();
  const handleDocumentsShow = () => setShowDocumentsModal(true);
  const handleDocumentsClose = () => setShowDocumentsModal(false);
  const [showDnacprConfirmModal, setShowDnacprConfirmModal] = useState(false);
  const [showLpaConfirmModal, setShowLpaConfirmModal] = useState(false);
  const [showDolsConfirmModal, setShowDolsConfirmModal] = useState(false);
  const [selectedDnacpr, setSelectedDnacpr] = useState(null);
  const [selectedLpa, setSelectedLpa] = useState(null);
  const [selectedDols, setSelectedDols] = useState(null);
  const [showEditDnacprModal, setShowEditDnacprModal] = useState(false);
  const [showEditLpaModal, setShowEditLpaModal] = useState(false);
  const [showEditDolsModal, setShowEditDolsModal] = useState(false);
  const [dnacprEnabled, setDnacprEnabled] = useState(false);

  // DNACPR
  const { data: allDnacprData, refetch: refecthDnacpr } = useQuery(
    [QK.dnacpr],
    () => getDnacpr(clientId)
  );

  const postDnacprMutation = useMutation(createDnacpr, {
    onSuccess: () => {
      enqueueSnackbar("DNACPR created successfully", { variant: "success" });
      refecthDnacpr();
    },
    onError: () => {
      enqueueSnackbar("Failed to create DNACPR", { variant: "error" });
    },
  });

  const handleDnacprSubmit = (e) => {
    e.preventDefault();
    const dnacprFile = e.target.dnacprFile.files[0];
    const dnacprData = new FormData();

    if (dnacprFile) {
      dnacprData.append("cert", dnacprFile);
    }
    dnacprData.append("start_date", e.target.dnacprStartDate.value);
    dnacprData.append("client", clientId);

    postDnacprMutation.mutate(dnacprData, {
      onSuccess: () => {
        e.target.reset();
      },
    });
  };

  const deleteDnacprMutation = useMutation(deleteDnacpr, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.dnacpr]);
      enqueueSnackbar("DNACPR deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete DNACPR", { variant: "error" });
    },
  });

  const handleDnacprDelete = (id) => {
    setSelectedDnacpr(id);
    setShowDnacprConfirmModal(true);
  };

  const handleDnacprDeleteClose = () => {
    setShowDnacprConfirmModal(false);
    setSelectedDnacpr(null);
  };

  const handleDnacprDeleteConfirm = () => {
    if (selectedDnacpr) {
      deleteDnacprMutation.mutate(selectedDnacpr);
    }
    handleDnacprDeleteClose();
  };

  const handleDnacprEdit = (dnacpr) => {
    setSelectedDnacpr(dnacpr);
    setShowEditDnacprModal(true);
  };

  const handleEditClose = () => {
    setShowEditDnacprModal(false);
    setSelectedDnacpr(null);
  };

  // LPA
  const { data: allLpaData, refetch: refecthLpa } = useQuery([QK.lpa], () =>
    getLpa(clientId)
  );

  const postLpaMutation = useMutation(createLPA, {
    onSuccess: () => {
      enqueueSnackbar("LPA created successfully", { variant: "success" });
      refecthLpa();
    },
    onError: () => {
      enqueueSnackbar("Failed to create LPA", { variant: "error" });
    },
  });

  const handleLpaSubmit = (e) => {
    e.preventDefault();
    const certHealth = e.target.lpaHealth.files[0];
    const certFinance = e.target.lpaFinance.files[0];

    const lpaData = new FormData();
    if (certHealth) {
      lpaData.append("cert_health", certHealth);
    }
    if (certFinance) {
      lpaData.append("cert_finance", certFinance);
    }
    lpaData.append("start_date", e.target.lpaData.value);
    lpaData.append("client", clientId);
    postLpaMutation.mutate(lpaData, {
      onSuccess: () => {
        e.target.reset();
      },
    });
  };

  const deleteLpaMutation = useMutation(deleteLpa, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.lpa]);
      enqueueSnackbar("LPA deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete LPA", { variant: "error" });
    },
  });

  const handleLpaDelete = (id) => {
    setSelectedLpa(id);
    setShowLpaConfirmModal(true);
  };

  const handleLpaDeleteClose = () => {
    setShowLpaConfirmModal(false);
    setSelectedLpa(null);
  };

  const handleLpaDeleteConfirm = () => {
    if (selectedLpa) {
      deleteLpaMutation.mutate(selectedLpa);
    }
    handleLpaDeleteClose();
  };

  const handleLpaEdit = (id) => {
    setSelectedLpa(id);
    setShowEditLpaModal(true);
  };

  const handleLpaEditClose = () => {
    setShowEditLpaModal(false);
    setSelectedLpa(null);
  };

  // Dols
  const { data: allDolsData, refetch: refetchDols } = useQuery([QK.dols], () =>
    getDols(clientId)
  );

  const deleteDolsMutation = useMutation(deleteDols, {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.dols]);
      enqueueSnackbar("DoLS deleted successfully", { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar("Failed to delete DoLS", { variant: "error" });
    },
  });

  const handleDolsDelete = (id) => {
    setSelectedDols(id);
    setShowDolsConfirmModal(true);
  };

  const handleDolsDeleteClose = () => {
    setShowDolsConfirmModal(false);
    setSelectedDols(null);
  };

  const handleDolsDeleteConfirm = () => {
    if (selectedDols) {
      deleteDolsMutation.mutate(selectedDols);
    }
    handleDolsDeleteClose();
  };

  const handleDolsEdit = (id) => {
    setSelectedDols(id);
    setShowEditDolsModal(true);
  };

  const SectionHeader = ({ icon, title, subtitle, action }) => (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      p: 2.5,
      pb: 1.5,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: "10px",
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1A1A2E">
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </Box>
  );

  const renderFileLink = (url, label = "Open File") => {
    if (!url) return <Typography variant="body2" color="text.secondary">No File</Typography>;
    return (
      <Link href={url} target="_blank" download sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}>
        <FileIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        <Typography variant="body2" sx={{ color: theme.palette.primary.main, fontWeight: 500 }}>
          {label}
        </Typography>
      </Link>
    );
  };

  // DNACPR Table Headers
  const dnacprHeaders = [
    { key: "sl_no", label: "SL NO" },
    { key: "file", label: "File" },
    { key: "start_date", label: "Start Date" },
  ];

  const dnacprRenderCell = (item, header) => {
    if (header.key === "sl_no") {
      const index = allDnacprData?.findIndex(d => d.id === item.id);
      return index + 1;
    }
    if (header.key === "file") {
      return renderFileLink(item.cert);
    }
    if (header.key === "start_date") {
      return item.start_date;
    }
    return item[header.key] || "-";
  };

  // LPA Table Headers
  const lpaHeaders = [
    { key: "sl_no", label: "SL NO" },
    { key: "cert_health", label: "Cert Health" },
    { key: "cert_finance", label: "Cert Finance" },
    { key: "start_date", label: "Start Date" },
  ];

  const lpaRenderCell = (item, header) => {
    if (header.key === "sl_no") {
      const index = allLpaData?.findIndex(d => d.id === item.id);
      return index + 1;
    }
    if (header.key === "cert_health") {
      return renderFileLink(item.cert_health);
    }
    if (header.key === "cert_finance") {
      return renderFileLink(item.cert_finance);
    }
    if (header.key === "start_date") {
      return item.start_date;
    }
    return item[header.key] || "-";
  };

  // DoLS Table Headers
  const dolsHeaders = [
    { key: "application_date", label: "Application Date" },
    { key: "issue_date", label: "Issue Date" },
    { key: "renewal_date", label: "Renewal Date" },
    { key: "status", label: "Status" },
    { key: "note_message", label: "Comment" },
    { key: "reported_to_chc", label: "Reported to CHC" },
    { key: "file", label: "File" },
  ];

  const dolsRenderCell = (item, header) => {
    if (header.key === "status") {
      return (
        <Chip
          label={item.status || "N/A"}
          size="small"
          sx={{
            bgcolor: item.status === "CURRENT" 
              ? alpha(theme.palette.success.main, 0.1) 
              : alpha(theme.palette.grey[500], 0.1),
            color: item.status === "CURRENT" 
              ? theme.palette.success.main 
              : theme.palette.grey[600],
            fontWeight: 600,
            fontSize: '0.65rem',
          }}
        />
      );
    }
    if (header.key === "reported_to_chc") {
      return item.reported_to_CHC === true ? "YES" : item.reported_to_CHC === false ? "NO" : "N/A";
    }
    if (header.key === "file") {
      return renderFileLink(item.dols_cert);
    }
    return item[header.key] || "-";
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* DNACPR Card */}
      <Card sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <SectionHeader
          icon={<DnacprIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />}
          title="Do Not Attempt Cardio-Pulmonary Resuscitation (DNACPR)"
          subtitle="Manage DNACPR records"
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setDnacprEnabled(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#2469F0',
                '&:hover': { bgcolor: '#1A5BD4' },
              }}
            >
              Add DNACPR
            </Button>
          }
        />

        <CardContent sx={{ p: 2.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={dnacprEnabled}
                onChange={() => setDnacprEnabled(!dnacprEnabled)}
                name="dnacprToggle"
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight={600}>
                Is there a DNACPR?
              </Typography>
            }
            sx={{ mb: 2 }}
          />

          {dnacprEnabled && (
            <Box component="form" onSubmit={handleDnacprSubmit}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    type="file"
                    name="dnacprFile"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
                <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                  <TextField
                    fullWidth
                    type="date"
                    name="dnacprStartDate"
                    label="DNACPR Start Date"
                    InputLabelProps={{ shrink: true }}
                    required
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Box>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={postDnacprMutation.isLoading}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: '#2469F0',
                    '&:hover': { bgcolor: '#1A5BD4' },
                  }}
                >
                  {postDnacprMutation.isLoading ? "Submitting..." : "Submit DNACPR"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setDnacprEnabled(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: '#D0D5DD',
                    color: '#344054',
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Box>
          )}

          {allDnacprData?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <DataTable
                data={allDnacprData}
                headers={dnacprHeaders}
                renderCell={dnacprRenderCell}
                onView={null}
                onUpdate={handleDnacprEdit}
                onDelete={handleDnacprDelete}
                canUpdate={true}
                canDelete={true}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* LPA Card */}
      <Card sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <SectionHeader
          icon={<LpaIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />}
          title="Lasting Power of Attorney (LPA)"
          subtitle="Manage LPA records"
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => document.getElementById('lpaForm').scrollIntoView({ behavior: 'smooth' })}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#2469F0',
                '&:hover': { bgcolor: '#1A5BD4' },
              }}
            >
              Add LPA
            </Button>
          }
        />

        <CardContent sx={{ p: 2.5 }}>
          <Box id="lpaForm" component="form" onSubmit={handleLpaSubmit}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="file"
                  name="lpaHealth"
                  label="Upload Cert Health"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="file"
                  name="lpaFinance"
                  label="Upload Cert Finance"
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '200px' }}>
                <TextField
                  fullWidth
                  type="date"
                  name="lpaData"
                  label="LPA Start Date"
                  InputLabelProps={{ shrink: true }}
                  required
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />
              </Box>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={postLpaMutation.isLoading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#2469F0',
                '&:hover': { bgcolor: '#1A5BD4' },
              }}
            >
              {postLpaMutation.isLoading ? "Submitting..." : "Submit LPA"}
            </Button>
          </Box>

          {allLpaData?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <DataTable
                data={allLpaData}
                headers={lpaHeaders}
                renderCell={lpaRenderCell}
                onView={null}
                onUpdate={handleLpaEdit}
                onDelete={handleLpaDelete}
                canUpdate={true}
                canDelete={true}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DoLS Card */}
      <Card sx={{
        mb: 3,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.04)',
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}>
        <SectionHeader
          icon={<DolsIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />}
          title="Deprivation of Liberty Safeguard (DoLS)"
          subtitle="Manage DoLS records"
          action={
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleDocumentsShow}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: '#2469F0',
                '&:hover': { bgcolor: '#1A5BD4' },
              }}
            >
              Add DoLS
            </Button>
          }
        />

        <CardContent sx={{ p: 2.5 }}>
          <DocumentsModal
            show={showDocumentsModal}
            onHide={handleDocumentsClose}
            refetchDols={refetchDols}
          />

          {allDolsData?.length > 0 ? (
            <DataTable
              data={allDolsData}
              headers={dolsHeaders}
              renderCell={dolsRenderCell}
              onView={null}
              onUpdate={handleDolsEdit}
              onDelete={handleDolsDelete}
              canUpdate={true}
              canDelete={true}
            />
          ) : (
            <Box sx={{
              py: 4,
              textAlign: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.divider, 0.2)}`,
            }}>
              <Typography variant="body2" color="text.secondary">
                No DoLS records found. Click "Add DoLS" to create one.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirm Modals */}
      <ConfirmModal
        show={showDnacprConfirmModal}
        handleClose={handleDnacprDeleteClose}
        handleConfirm={handleDnacprDeleteConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this DNACPR?"
      />

      <ConfirmModal
        show={showLpaConfirmModal}
        handleClose={handleLpaDeleteClose}
        handleConfirm={handleLpaDeleteConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this LPA?"
      />

      <ConfirmModal
        show={showDolsConfirmModal}
        handleClose={handleDolsDeleteClose}
        handleConfirm={handleDolsDeleteConfirm}
        title="Delete Confirmation"
        body="Are you sure you want to delete this DoLS?"
      />

      {/* Edit Modals */}
      {selectedDnacpr && (
        <DnacprEditModal
          show={showEditDnacprModal}
          handleClose={() => setShowEditDnacprModal(false)}
          data={selectedDnacpr}
          refecthDnacpr={refecthDnacpr}
          handleEditSubmit={() => handleEditClose()}
        />
      )}

      {selectedLpa && (
        <EditLpaModal
          show={showEditLpaModal}
          handleClose={() => setShowEditLpaModal(false)}
          data={selectedLpa}
          refecthLpa={refecthLpa}
          handleEditSubmit={() => handleLpaEditClose()}
        />
      )}

      {selectedDols && (
        <EditDolsModal
          show={showEditDolsModal}
          handleClose={() => setShowEditDolsModal(false)}
          selectedDols={selectedDols}
          refetchDols={refetchDols}
        />
      )}
    </Container>
  );
};

export default Documents;